import { useEffect, useRef } from 'react';

/**
 * Auto-resize an element's height based on its scroll content.
 *
 * @typedef {Object} UseAutosizeElementProps
 * @property {React.RefObject<HTMLElement>} ref
 *   Ref to the element being sized (e.g. a textarea). Required.
 * @property {string} [value]
 *   The element's current value. Used as the effect dependency that triggers
 *   a re-measure on every change.
 * @property {boolean} [isManual=false]
 *   When `false` (default): classic auto-grow — the element's height tracks
 *   `scrollHeight` on every value change.
 *   When `true`: the element behaves as if `floorHeightRef.current` were a
 *   min-height. Autosize only kicks in when content was previously fitting
 *   inside the floor and grows past it. If content was already overflowing
 *   when the floor was set (pinned mode), height stays at the floor and the
 *   internal scrollbar handles overflow.
 * @property {number} [maxHeight]
 *   Maximum height (in px) the element can grow to. If omitted, no cap.
 * @property {React.MutableRefObject<number|null>} [floorHeightRef]
 *   External ref holding the "min height" (the height set by a manual resize
 *   handle). The hook reads it to know where to pin in manual mode and
 *   initializes it to the element's `offsetHeight` on first run if null.
 *   Optional — when omitted, the hook uses an internal ref.
 * @property {React.MutableRefObject<boolean|null>} [isPinnedRef]
 *   External ref tracking whether the element is currently pinned at the
 *   floor (i.e. content was already overflowing when the floor was set, so
 *   no autosize should fire). The hook flips it back to `false` automatically
 *   when content shrinks back inside the floor. Optional — when omitted, the
 *   hook uses an internal ref.
 *
 * @param {UseAutosizeElementProps} props
 */

const useAutosizeElement = ({
  ref,
  value,
  isManual = false,
  maxHeight,
  floorHeightRef,
  isPinnedRef
}) => {
  const localFloorRef = useRef(null);
  const localPinnedRef = useRef(null);
  const floorRef = floorHeightRef ?? localFloorRef;
  const pinnedRef = isPinnedRef ?? localPinnedRef;
  const cap = maxHeight ?? Infinity;

  useEffect(() => {
    const element = ref?.current;
    if (!element || !element.style) return;

    if (isManual) {
      if (floorRef.current == null) floorRef.current = element.offsetHeight;
      const floor = floorRef.current;
      const scrollHeight = element.scrollHeight;

      if (pinnedRef.current == null) pinnedRef.current = scrollHeight > floor;

      if (scrollHeight <= floor) {
        pinnedRef.current = false;
        element.style.height = `${floor}px`;
      } else if (pinnedRef.current) {
        element.style.height = `${floor}px`;
      } else {
        element.style.height = `${Math.min(scrollHeight, cap)}px`;
      }
      return;
    }

    if (value?.length > 0) {
      element.style.height = '';
      element.style.height = `${Math.min(element.scrollHeight, cap)}px`;
    }
  }, [ref, value, isManual, cap, floorRef, pinnedRef]);
};

export default useAutosizeElement;
